import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import Footer from './Footer';
import SEO from './SEO';

const FAQ_DATA = [
    {
        q: "Token nedir ve nasıl çalışır?",
        a: "Token, çeviri işlemlerinizde kullandığınız bir birimdir. Her karakter 1 token karşılık gelir. Örneğin 50.000 karakterlik bir doküman için 50.000 token harcanır. Token'larınızı profil sayfanızdan satın alabilirsiniz."
    },
    {
        q: "Hangi dosya formatları destekleniyor?",
        a: "Şu anda PDF (.pdf), Word (.docx), EPUB (.epub) ve düz metin (.txt) dosyaları desteklenmektedir. Maksimum dosya boyutu 50 MB'dır."
    },
    {
        q: "OCR nedir ve neden daha pahalı?",
        a: "OCR (Optik Karakter Tanıma), taranmış veya görsel tabanlı PDF'lerden metin çıkarmak için kullanılan bir teknolojidir. Normal PDF'lere kıyasla çok daha fazla işlem gücü gerektirdiğinden fiyatı %30 daha yüksektir (1.3× token çarpanı)."
    },
    {
        q: "Dosyalarım güvende mi?",
        a: "Evet. Çevirilen dosyalarınız güvenlik amacıyla yalnızca 24 saat sunucularımızda tutulur ve ardından otomatik olarak silinir. Belgelerinizi işleme dışında başka bir amaç için kullanmıyoruz."
    },
    {
        q: "Hangi dillere çeviri yapılabiliyor?",
        a: "100'den fazla dile çeviri desteklenmektedir. Türkçe, İngilizce, Almanca, Fransızca, İspanyolca, Arapça, Japonca ve daha fazlası dahil tüm yaygın diller mevcuttur."
    },
    {
        q: "Çeviri ne kadar sürer?",
        a: "Dosya boyutuna göre değişir. Küçük dosyalar (birkaç MB altı) birkaç dakika içinde tamamlanır. Büyük dosyalar kuyruğa alınır ve tamamlandığında size e-posta ile bildirim gelir."
    },
    {
        q: "Ödeme nasıl gerçekleşiyor?",
        a: "Token satın alımları entegre Shopify ödeme altyapısı ile güvenli şekilde gerçekleştirilir. Kredi kartı, banka kartı ve birçok ödeme yöntemi desteklenmektedir."
    },
    {
        q: "Giriş yapamıyorum, ne yapmalıyım?",
        a: "E-posta adresinizi doğrulamış olmanız gerekir. Giriş sayfasındaki 'Doğrulama mailini tekrar gönder' seçeneğini kullanabilirsiniz. Sorun devam ederse iletişim sayfamızdan bize ulaşın."
    },
    {
        q: "Hesabım neden kilitlendi?",
        a: "Güvenliğiniz için arka arkaya 10 hatalı giriş denemesinden sonra hesabınız 30 dakika süreyle otomatik olarak kilitlenir. Bu süre dolduğunda tekrar giriş yapabilirsiniz."
    },
];

const FAQItem = ({ q, a }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="border border-slate-200 rounded-2xl overflow-hidden hover:border-blue-200 transition-colors">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-slate-50 transition-colors"
            >
                <span className="font-semibold text-slate-800 text-base pr-4">{q}</span>
                {open ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />}
            </button>
            {open && (
                <div className="px-6 pb-6 bg-white">
                    <p className="text-slate-600 leading-relaxed">{a}</p>
                </div>
            )}
        </div>
    );
};

const FAQ = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Sıkça Sorulan Sorular (SSS) — CeviriPro"
                description="CeviriPro hakkında merak ettiklerinizi yanıtladık. Desteklenen dosya formatları, fiyatlandırma, OCR teknolojisi ve daha fazlası hakkında sıkça sorulan sorular."
                canonical="/sss"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": FAQ_DATA.map(item => ({
                        "@type": "Question",
                        "name": item.q,
                        "acceptedAnswer": { "@type": "Answer", "text": item.a }
                    }))
                }}
            />
            {/* Nav */}
            <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="bg-gradient-to-r from-blue-600 to-green-600 p-2 rounded-xl">
                            <Globe className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-bold text-slate-800 text-lg">CeviriPro</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link to="/login" className="text-slate-600 hover:text-blue-900 text-sm font-medium transition-colors">Giriş</Link>
                        <Link to="/register" className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all">Ücretsiz Başla</Link>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-green-50 py-16 px-4 text-center">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-md flex items-center justify-center mx-auto mb-6">
                    <HelpCircle className="w-8 h-8 text-blue-600" />
                </div>
                <span className="inline-block px-4 py-1.5 bg-white border border-blue-200 text-blue-600 text-sm font-semibold rounded-full mb-4">SSS</span>
                <h1 className="text-4xl font-black text-slate-900 mb-4">Sıkça Sorulan Sorular</h1>
                <p className="text-xl text-slate-600 max-w-xl mx-auto">Merak ettiklerinizi hepsini burada yanıtladık.</p>
            </section>

            {/* Questions */}
            <section className="py-16 px-4">
                <div className="max-w-3xl mx-auto space-y-3">
                    {FAQ_DATA.map((item, i) => (
                        <FAQItem key={i} q={item.q} a={item.a} />
                    ))}
                </div>

                <div className="max-w-3xl mx-auto mt-12 text-center p-8 bg-gradient-to-r from-blue-50 to-green-50 rounded-3xl border border-blue-100">
                    <h3 className="text-xl font-bold text-slate-900 mb-3">Bulamadığınız bir şey mi var?</h3>
                    <p className="text-slate-600 mb-6">Sorularınızı doğrudan bize iletebilirsiniz.</p>
                    <Link to="/iletisim" className="inline-block px-6 py-3 bg-gradient-to-r from-blue-600 to-green-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all">
                        Bize Ulaşın
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default FAQ;
